'use client';

import { createContext, useContext, useState, useCallback } from 'react';
import { Confirm } from './confirm';
import type { ConfirmState, ConfirmOptions } from '@/types/confirm.types';

interface ConfirmContextValue {
	confirm: (message: string, options?: ConfirmOptions) => Promise<boolean>;
}

const ConfirmContext = createContext<ConfirmContextValue | undefined>(undefined);

/**
 * ConfirmProvider 컴포넌트
 * 전역 Confirm 모달 상태를 관리하고 useConfirm 훅을 통해 제공합니다.
 *
 * @example
 * ```tsx
 * <ConfirmProvider>
 *   <App />
 * </ConfirmProvider>
 * ```
 */
export function ConfirmProvider({ children }: { children: React.ReactNode }) {
	const [state, setState] = useState<ConfirmState>({
		isOpen: false,
		message: '',
		title: '확인',
		variant: 'info',
		confirmText: '확인',
		cancelText: '취소',
		isLoading: false,
		resolve: null,
	});

	const confirm = useCallback(
		(message: string, options?: ConfirmOptions): Promise<boolean> => {
			return new Promise<boolean>((resolve) => {
				setState({
					isOpen: true,
					message,
					title: options?.title ?? '확인',
					variant: options?.variant ?? 'info',
					confirmText: options?.confirmText ?? '확인',
					cancelText: options?.cancelText ?? '취소',
					isLoading: options?.isLoading ?? false,
					resolve,
				});
			});
		},
		[]
	);

	const handleClose = useCallback(() => {
		if (state.resolve) {
			state.resolve(false);
		}
		setState((prev) => ({
			...prev,
			isOpen: false,
			resolve: null,
		}));
	}, [state.resolve]);

	const handleConfirm = useCallback(() => {
		if (state.resolve) {
			state.resolve(true);
		}
		setState((prev) => ({
			...prev,
			isOpen: false,
			resolve: null,
		}));
	}, [state.resolve]);

	return (
		<ConfirmContext.Provider value={{ confirm }}>
			{children}
			<Confirm
				isOpen={state.isOpen}
				onClose={handleClose}
				onConfirm={handleConfirm}
				title={state.title}
				message={state.message}
				confirmText={state.confirmText}
				cancelText={state.cancelText}
				variant={state.variant}
				isLoading={state.isLoading}
			/>
		</ConfirmContext.Provider>
	);
}

/**
 * useConfirm 훅
 * Confirm Provider의 컨텍스트를 사용하여 confirm 함수를 반환합니다.
 *
 * @example
 * ```tsx
 * const confirm = useConfirm();
 * const result = await confirm('정말로 삭제하시겠습니까?');
 * if (result) {
 *   // 삭제 로직 실행
 * }
 * ```
 */
export function useConfirm() {
	const context = useContext(ConfirmContext);
	if (context === undefined) {
		throw new Error('useConfirm must be used within a ConfirmProvider');
	}
	return context.confirm;
}
